import { format } from "date-fns";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { Plus, X } from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_WHITE, ACTION_BTN_ICON } from "../styles/actionButtons";
import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../App.css";
import AddEventModal from "../components/AddEventModal";
import PageHeader from "../components/PageHeader";
import EditEventModal from "../components/EditEventModal";
import ReminderModal from "../components/ReminderModal";
import WeeklyCalendar from "../components/WeeklyCalendar";
import { useAuth } from "../context/AuthContext";
import { auth, db } from "../firebase";
import {
  deleteEvent,
  fetchFirestoreEvents,
  fetchGoogleEventsFromFirestore,
} from "../services/firebaseService";
import {
  signInWithGoogle,
  syncGoogleEvents,
} from "../services/googleCalendarService";
import jsPDF from "jspdf";
import { formatCaseTitle } from "../utils/courtMapping";
import { Timestamp } from "firebase/firestore";
import './styles.css';

// Google's brand mark. Each path carries its own fill rather than currentColor,
// which also keeps it clear of the `.light-mode .text-white svg` colour reset.
// stroke must be an inline style: `.light-mode svg { stroke: currentColor }`
// would otherwise outline every segment, and CSS beats a stroke="none" attribute.
const GoogleIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    style={{ stroke: "none" }}
    aria-hidden="true"
    focusable="false"
  >
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

function TaskOverview() {
  function ReminderInfoModal({ showModal, setShowModal, reminders }) {
    const [remindersWithNames, setRemindersWithNames] = useState([]);

    useEffect(() => {
      const fetchNames = async () => {
        const uniqueRecipientIds = [
          ...new Set(reminders.map((r) => r.recipientId)),
        ];
        const userPromises = uniqueRecipientIds.map((id) =>
          getDoc(doc(db, "lawyers", id))
        );
        const userDocs = await Promise.all(userPromises);
        const userMap = {};
        userDocs.forEach((docSnap) => {
          if (docSnap.exists()) {
            userMap[docSnap.id] = docSnap.data().name || "Unknown";
          }
        });
        const remindersWithNames = reminders.map((reminder) => ({
          ...reminder,
          recipientName: userMap[reminder.recipientId] || "Unknown",
        }));
        setRemindersWithNames(remindersWithNames);
      };
      if (showModal) fetchNames();
    }, [showModal, reminders]);

    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[100] p-4 backdrop-blur-sm">
        <div className="bg-gray-800 rounded-lg shadow-xl p-5 md:p-6 w-full max-w-xl max-h-[90vh] flex flex-col text-white border border-gray-700">
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-600">
            <h2 className="text-xl font-semibold">Reminder Details</h2>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto">
            {remindersWithNames.length === 0 ? (
              <p className="text-center text-gray-400">No reminders found.</p>
            ) : (
              <ul className="space-y-4">
                {remindersWithNames.map((reminder, index) => (
                  <li key={index} className="bg-gray-700 p-3 rounded">
                    <p>
                      <strong>Recipient:</strong> {reminder.recipientName}
                    </p>
                    <p>
                      <strong>Reminder Time:</strong>{" "}
                      {format(reminder.reminderTime.toDate(), "PPpp")}
                    </p>
                    <p>
                      <strong>Status:</strong> {reminder.status}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }


  function EventAddedInfoModal({ showModal, setShowModal }) {
    if (!showModal) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[100] px-4 sm:px-0">
        <div
          className="absolute inset-0 bg-black bg-opacity-70"
          onClick={() => setShowModal(false)}
        ></div>
        <div className="bg-gray-800 rounded-lg shadow-xl p-5 md:p-6 w-full max-w-md max-h-[90vh] flex flex-col text-white border border-gray-700 z-[101]">
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-600">
            <h2 className="text-xl font-semibold">Event Created</h2>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-grow overflow-y-auto">
            <p className="text-center text-gray-300">
              Your event has been created successfully! Default reminders are set: one at the event time and one 5 minutes before. To change or add new reminders, click the bell icon in the event details.
            </p>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowModal(false)}
              className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md font-semibold hover:bg-yellow-600 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firestoreEventsData, setFirestoreEventsData] = useState([]);
  const [googleEventsData, setGoogleEventsData] = useState([]);
  const [totalEvents, setTotalEvents] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [eventForReminder, setEventForReminder] = useState(null);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [remindersByEvent, setRemindersByEvent] = useState({});
  const [showReminderInfoModal, setShowReminderInfoModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showEventAddedInfoModal, setShowEventAddedInfoModal] = useState(false);

  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  const formatDateForDisplay = (date) => format(new Date(date), "yyyy-MM-dd");

  const fetchRemindersForEvents = async (eventIds, userId) => {
    if (eventIds.length === 0) return [];
    const reminders = [];
    const chunks = [];
    for (let i = 0; i < eventIds.length; i += 10) {
      chunks.push(eventIds.slice(i, i + 10));
    }
    const promises = chunks.map(async (chunk) => {
      const q = query(
        collection(db, "eventReminders"),
        where("scheduledBy", "==", userId),
        where("eventId", "in", chunk)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    });
    const results = await Promise.all(promises);
    results.forEach((chunk) => {
      reminders.push(...chunk);
    });
    return reminders;
  };

  useEffect(() => {
    if (userId) loadEvents();
  }, [userId, selectedDate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const { events: firestoreEvents, stats: eventStats } =
        await fetchFirestoreEvents();
      
      console.log("Firestore Events:", firestoreEvents);
      console.log("First Event:", firestoreEvents[0]);
      setFirestoreEventsData(firestoreEvents);
      setStats({
        total: eventStats.total,
        pending: eventStats.left,
        done: eventStats.done,
      });
      const googleEventsData = await fetchGoogleEventsFromFirestore();
      setGoogleEventsData(googleEventsData);
      const formattedDate = formatDateForDisplay(selectedDate);
      const filteredFirestoreEvents = firestoreEvents.filter(
        (event) => event.date === formattedDate
      );
      const filteredGoogleEvents = googleEventsData.filter(
        (event) => event.date === formattedDate
      );
      setEvents(filteredFirestoreEvents);
      setGoogleEvents(filteredGoogleEvents);
      setLoading(false);

      const allEventIds = [
        ...filteredFirestoreEvents.map((e) => e.docId),
        ...filteredGoogleEvents.map((e) => e.id),
      ];
      const reminders = await fetchRemindersForEvents(allEventIds, userId);
      const remindersMap = {};
      reminders.forEach((reminder) => {
        const eventId = reminder.eventId;
        if (!remindersMap[eventId]) {
          remindersMap[eventId] = [];
        }
        remindersMap[eventId].push(reminder);
      });
      setRemindersByEvent(remindersMap);
    } catch (err) {
      console.error("Error loading events:", err);
      setError("Failed to load events. Please try again.");
      setLoading(false);
    }
  };

  const handleSyncGoogle = async () => {
    try {
      // Force sync when user clicks the button (bypass 5-minute throttle)
      const syncResult = await syncGoogleEvents(true);

      if (syncResult.success) {
        toast.success(`Synced ${syncResult.eventCount || 0} Google events`);
        setLastSyncTime(Date.now());
        await loadEvents();
      } else {
        // Check if re-authentication is needed (token missing or invalid)
        const errorMessage = syncResult.error?.message || "";
        if (errorMessage.includes("No access token") || errorMessage.includes("invalid_token") || syncResult.needReauth) {
          toast.info("Connecting to Google Calendar...");
          const signInResult = await signInWithGoogle();
          if (signInResult.success) {
            const retrySync = await syncGoogleEvents();
            if (retrySync.success) {
              toast.success(`Synced ${retrySync.eventCount || 0} Google events`);
              setLastSyncTime(Date.now());
              await loadEvents();
            } else {
              toast.error("Failed to sync Google events after sign-in");
            }
          } else {
            toast.error("Failed to connect Google account");
          }
        } else {
          toast.error("Failed to sync Google events");
        }
      }
    } catch (err) {
      console.error("Error syncing Google events:", err);
      toast.error("Failed to sync Google events");
    }
  };

  useEffect(() => {
    setTotalEvents([...firestoreEventsData, ...googleEventsData]);
  }, [firestoreEventsData, googleEventsData]);

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(eventId);
      setEvents(events.filter((event) => event.docId !== eventId));
      const eventToDelete = events.find((event) => event.docId === eventId);
      if (eventToDelete) {
        setStats((prev) => ({
          ...prev,
          total: prev.total - 1,
          pending:
            eventToDelete.status === "left" ? prev.pending - 1 : prev.pending,
          done: eventToDelete.status === "done" ? prev.done - 1 : prev.done,
        }));
      }
      toast.success("Event deleted successfully");
    } catch (err) {
      console.error("Error deleting event:", err);
      toast.error("Failed to delete event");
    }
  };

  const handleEditEvent = (event) => {
    if (event["Case No"] && !event.isGoogleEvent) {
      getCaseByCaseNumber(event["Case No"]).then((caseData) => {
        if (caseData) navigate(`/portal/case/${caseData.documentId}`);
        else {
          setCurrentEvent(event);
          setShowEditModal(true);
        }
      });
    } else if (event.isGoogleEvent && !event["Case No"]) {
      navigate(`/portal/AddCase`);
    } else {
      setCurrentEvent(event);
      setShowEditModal(true);
    }
  };

  const getCaseByCaseNumber = async (caseNo) => {
    try {
      const casesRef = collection(db, "pending");
      const q = query(casesRef, where("caseNo", "==", caseNo));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { documentId: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching case:", error);
      return null;
    }
  };

  const handleOpenReminderModal = (event) => {
    setEventForReminder(event);
    setShowReminderModal(true);
  };


  const handleSendReminders = async (reminderData) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error("You must be logged in to schedule reminders.");
      return;
    }

    if (!reminderData || !reminderData.event || !reminderData.recipients || reminderData.recipients.length === 0) {
      toast.error("Could not schedule reminders due to invalid data.");
      return;
    }

    const now = Date.now();
    if (reminderData.event.time1 <= now) {
      toast.error("Cannot schedule reminder for past events.");
      return;
    }

    setIsSendingReminder(true);
    try {
      const eventRemindersRef = collection(db, "eventReminders");
      const externalRemindersRef = collection(db, "externalEventReminders");
      const batch = writeBatch(db);

      const reminderTime = Timestamp.fromDate(new Date(reminderData.reminderTime));
      const reminderTimeMs = reminderTime.toMillis();
      if (reminderTimeMs <= now) {
        throw new Error("Reminder time is in the past.");
      }

      for (const recipient of reminderData.recipients) {
        const eventId = reminderData.event.docId || reminderData.event.id;
        const recipientId = typeof recipient === "string" ? null : recipient.id;
        const phoneNumber = typeof recipient === "string" ? recipient : null;

        // Check for existing pending reminders
        if (recipientId) {
          const existingQuery = query(
            eventRemindersRef,
            where("eventId", "==", eventId),
            where("recipientId", "==", recipientId),
            where("status", "==", "pending")
          );
          const existingSnapshot = await getDocs(existingQuery);
          if (!existingSnapshot.empty) {
            console.log(`Reminder already exists for event ${eventId} and recipient ${recipientId}. Skipping.`);
            continue;
          }
        } else if (phoneNumber) {
          const existingQuery = query(
            externalRemindersRef,
            where("eventId", "==", eventId),
            where("phoneNumber", "==", phoneNumber),
            where("status", "==", "pending")
          );
          const existingSnapshot = await getDocs(existingQuery);
          if (!existingSnapshot.empty) {
            console.log(`Reminder already exists for event ${eventId} and phone ${phoneNumber}. Skipping.`);
            continue;
          }
        }

        const reminderDoc = {
          eventId: eventId,
          reminderTime: reminderTime,
          eventTitle: reminderData.event.title,
          caseNo: reminderData.event["Case No"] || "",
          status: "pending",
          createdAt: new Date(),
          scheduledBy: userId,
        };

        if (phoneNumber) {
          reminderDoc.phoneNumber = phoneNumber;
          const docRef = doc(externalRemindersRef);
          batch.set(docRef, reminderDoc);
        } else {
          reminderDoc.recipientId = recipientId;
          const docRef = doc(eventRemindersRef);
          batch.set(docRef, reminderDoc);
        }
      }

      await batch.commit();
      toast.success(`Scheduled reminders for ${reminderData.recipients.length} recipient(s).`);
      await loadEvents();
    } catch (error) {
      console.error("Error scheduling reminders:", error);
      toast.error(error.message || "Failed to schedule reminders.");
    } finally {
      setIsSendingReminder(false);
      setShowReminderModal(false);
      setEventForReminder(null);
    }
  };

  const handleCancelReminders = async (eventId) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel all reminders for this event?"
      )
    )
      return;
    try {
      const q = query(
        collection(db, "eventReminders"),
        where("eventId", "==", eventId),
        where("scheduledBy", "==", userId)
      );
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      snapshot.docs.forEach((doc) => {
        batch.update(doc.ref, { status: "cancelled" });
      });
      await batch.commit();
      toast.success("Reminders cancelled successfully");
      setRemindersByEvent((prev) => {
        const newReminders = { ...prev };
        delete newReminders[eventId];
        return newReminders;
      });
    } catch (err) {
      console.error("Error cancelling reminders:", err);
      toast.error("Failed to cancel reminders");
    }
  };

  const handleShowReminderInfo = (eventId) => {
    setSelectedEventId(eventId);
    setShowReminderInfoModal(true);
  };

  const handleExportCauseList = async () => {
    try {
      const relevantEvents = events.filter((event) => event["Case No"]);
      const caseDetailsPromises = relevantEvents.map((event) =>
        getCaseByCaseNumber(event["Case No"])
      );
      const caseDetails = await Promise.all(caseDetailsPromises);
      const validCaseDetails = caseDetails.filter((caseData) => caseData);

      if (validCaseDetails.length === 0) {
        toast.info("No cases to export for the selected date.");
        return;
      }

      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 20; // Further reduced to minimize space below header

      // --- Configuration ---
      const config = {
        lineHeight: 6, // Slightly reduced from 6.5
        topPadding: 4, // Reduced from 4
        bottomPadding: 0, // Reduced from 1 to eliminate bottom padding
        columnGap: 8, // Reduced from 10 to prevent text overflow
        fieldSpacing: 1, // Reduced from 1.5
        leftMargin: 10, // Increased from 5 to prevent text getting cut off at left edge
        columnWidth: 80, // Reduced from 85 to prevent text overflow
        spaceBetweenBoxes: 3, // Reduced from 10 to bring boxes closer together
      };

      const addHeader = () => {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(
          `Cause List for ${formatDateForDisplay(selectedDate)}`,
          config.leftMargin,
          15
        );
        doc.setFont("helvetica", "normal");
      };

      addHeader();

      validCaseDetails.forEach((caseData, index) => {
        // --- Calculate required height for this case ---
        let maxColumnContentHeight = 0;
        const columnContentHeights = [0, 0];

        const calculateFieldHeight = (text, fontSize) => {
          if (!text) return 0;
          doc.setFontSize(fontSize);
          const lines = doc.splitTextToSize(String(text), config.columnWidth);
          return lines.length * config.lineHeight;
        };

        // Calculate content heights for both columns
        [0, 1].forEach((colIndex) => {
          const fields =
            colIndex === 0
              ? [
                "caseNo",
                "previousHearingDate",
                "caseType",
                "courtName",
                "nextHearingDate",
              ]
              : ["petitionerName", "respondentName"];

          let currentColumnHeight = 0;
          fields.forEach((field, fieldIndex) => {
            const label = field.replace(/([A-Z])/g, " $1").toUpperCase() + ":";
            const value = caseData[field] || "N/A";
            currentColumnHeight += calculateFieldHeight(label, 10);
            currentColumnHeight += calculateFieldHeight(value, 8);
            if (fieldIndex < fields.length - 1) {
              currentColumnHeight += config.fieldSpacing;
            }
          });
          columnContentHeights[colIndex] = currentColumnHeight;
          maxColumnContentHeight = Math.max(
            maxColumnContentHeight,
            currentColumnHeight
          );
        });

        // Calculate the total height needed for the box
        const boxHeight =
          maxColumnContentHeight + config.topPadding + config.bottomPadding;

        // --- Check page space before adding box ---
        const spaceNeeded =
          boxHeight +
          (index < validCaseDetails.length - 1 ? config.spaceBetweenBoxes : 0);
        if (yPosition + spaceNeeded > pageHeight - 15) {
          // Reduced margin from 20 to 15
          doc.addPage();
          addHeader();
          yPosition = 20; // Position content immediately below header on new pages
        }

        // --- Draw bounding box ---
        doc.setDrawColor(0);
        doc.rect(config.leftMargin, yPosition, 180, boxHeight); // Reduced width from 190 to 180

        // --- Render columns independently ---
        [0, 1].forEach((colIndex) => {
          const fields =
            colIndex === 0
              ? [
                "caseNo",
                "previousHearingDate",
                "caseType",
                "courtName",
                "nextHearingDate",
              ]
              : ["petitionerName", "respondentName"];

          // Start rendering content at exact top padding
          let yCursor = yPosition + config.topPadding;
          const xPos =
            config.leftMargin +
            (colIndex === 0 ? 5 : config.columnWidth + config.columnGap + 5);

          fields.forEach((field, fieldIndex) => {
            const label = field.replace(/([A-Z])/g, " $1").toUpperCase() + ":";
            const value = caseData[field] || "N/A";

            // Label
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            const labelLines = doc.splitTextToSize(label, config.columnWidth);
            doc.text(labelLines, xPos, yCursor);
            yCursor += labelLines.length * config.lineHeight;

            // Value
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            const valueLines = doc.splitTextToSize(
              String(value),
              config.columnWidth
            );
            doc.text(valueLines, xPos, yCursor);
            yCursor += valueLines.length * config.lineHeight;

            // Add spacing only if it's not the last field
            if (fieldIndex < fields.length - 1) {
              yCursor += config.fieldSpacing;
            }
          });
        });

        // Update yPosition for the next box
        yPosition += boxHeight + config.spaceBetweenBoxes;
      });

      doc.save(`Cause_List_${formatDateForDisplay(selectedDate)}.pdf`);
    } catch (error) {
      console.error("Error exporting cause list:", error);
      toast.error("Failed to export cause list.");
    }
  };

  const handleDateChange = (date) => setSelectedDate(date);
  //const handleEventAdded = () => loadEvents();
  const handleEventAdded = async () => {
    await loadEvents();
    setShowEventAddedInfoModal(true);
  };
  const handleEventUpdated = () => loadEvents();

  const getEventDatesForCalendar = () => {
    return totalEvents.reduce((acc, event) => {
      const dateStr = event?.date;
      if (!acc[dateStr]) acc[dateStr] = [];
      acc[dateStr].push(event?.title);
      return acc;
    }, {});
  };

  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const dateStr = format(date, "yyyy-MM-dd");
      const eventDates = getEventDatesForCalendar();
      const dayEvents = eventDates[dateStr];
      if (dayEvents) {
        return (
          <div className="flex justify-center mt-1">
            {dayEvents.length > 3 ? (
              <div className="flex space-x-1">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
              </div>
            ) : (
              <div className="flex space-x-1">
                {dayEvents.map((_, index) => (
                  <div
                    key={index}
                    className="h-2 w-2 rounded-full bg-blue-500"
                  ></div>
                ))}
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  if (loading && !events.length) {
    return (
      <div className="min-h-screen text-white flex flex-col justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
        <p className="mt-4">Loading your events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen text-white flex flex-col justify-center items-center">
        <p className="text-red-500">{error}</p>
        <button
          onClick={loadEvents}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="crm-compact min-h-screen text-white flex flex-col">
      <ToastContainer position="top-right" theme="dark" />
      <div
        className={`${showAddModal || showEditModal || showReminderModal ? "blur-sm" : ""
          } transition-all duration-200`}
      >
        {/* Sidebar + topbar are provided by PortalShell — no in-page nav. */}
        <main className="bg-transparent px-4 sm:px-6 lg:px-8 py-4 z-10">
          <PageHeader
            title="Calendar"
            subtitle="Your tasks, events and hearing reminders"
            className="mb-4"
            actions={
              <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowAddModal(true)}
                    className={ACTION_BTN_GOLD}
                  >
                    <Plus className={ACTION_BTN_ICON} />
                    Add Event
                  </button>
                  {/* Tooltip now hangs off the Sync button itself (the separate
                      info icon is gone). It still drops BELOW: the shell's <main>
                      is the scroll container (overflow-y auto / x hidden), so a
                      tooltip above this row gets clipped out of view. */}
                  <div className="relative group w-full sm:w-auto">
                    <button
                      onClick={handleSyncGoogle}
                      className={`w-full ${ACTION_BTN_WHITE}`}
                    >
                      <GoogleIcon className={`${ACTION_BTN_ICON} shrink-0`} />
                      Sync Google
                    </button>
                    <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 pointer-events-none w-56">
                      <p>Sync your google calendar</p>
                      <p className="mt-2">NOTE: Change your google account through accounts page.</p>
                      <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-800"></div>
                    </div>
                  </div>
                </div>
            }
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                <div className="bg-white bg-opacity-10 rounded-lg px-3 sm:px-4 py-2.5">
                  <div className="text-lg sm:text-xl font-bold text-center">
                    {stats.total}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 text-center">
                    Total
                  </div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg px-3 sm:px-4 py-2.5">
                  <div className="text-lg sm:text-xl font-bold text-center">
                    {stats.pending}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 text-center">
                    Pending
                  </div>
                </div>
                <div className="bg-white bg-opacity-10 rounded-lg px-3 sm:px-4 py-2.5">
                  <div className="text-lg sm:text-xl font-bold text-center">
                    {stats.done}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-400 text-center">
                    Done
                  </div>
                </div>
              </div>
              {/* i want to add scroll in below section */}

              <div className="space-y-3 max-h-[calc(100%-2rem)] overflow-y-auto lg:max-h-[400px]">
                {events.length === 0 && googleEvents.length === 0 ? (
                  <div className="text-center py-6 bg-white bg-opacity-5 rounded-lg no-events-card">
                    <p className="no-events-text">No events for {formatDateForDisplay(selectedDate)}</p>
                  </div>
                ) : (
                  <>
                    {events.map((event) => {
                      const eventId = event.docId || event.id;
                      const eventReminders = remindersByEvent[eventId] || [];
                      console.log("Event data for", eventId, ":", event);

                      // Process title and description
                      let displayTitle = event.title;
                      let displayDescription = event.description || "";

                      // Check if title starts with "Reminder:" or "Update Next Hearing:"
                      if (event.title.startsWith("Reminder:")) {
                        displayTitle = event.title.replace("Reminder:", "").trim();
                        displayDescription = event.title; // Full title goes to description
                      } else if (event.title.startsWith("Update Next Hearing:")) {
                        displayTitle = event.title.replace("Update Next Hearing:", "").trim();
                        displayDescription = event.title; // Full title goes to description
                      }

                      return (

                        <div
                          key={event.id}
                          className="flex items-center justify-between bg-white bg-opacity-5 p-3 sm:p-4 rounded-lg"
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-1">
                            <div className="w-1 h-10 sm:h-12 bg-yellow-500 rounded-full"></div>
                            <div className="flex-1 min-w-0">
                              <div className="event-title text-sm sm:text-base font-semibold">
                                {formatCaseTitle(displayTitle)}
                              </div>
                              {displayDescription && (
                                <div className="event-description text-xs sm:text-sm text-gray-300 italic mt-1 line-clamp-2">
                                  {displayDescription}
                                </div>
                              )}
                              <div className="event-time text-xs sm:text-sm text-gray-400 mt-1">
                                {event.time}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {eventReminders.length > 0 && (
                              <>
                                <button
                                  className="text-yellow-400 light-icon-warning"
                                  onClick={() => handleCancelReminders(eventId)}
                                  title="Cancel Reminders"
                                >
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                                <button
                                  className="light-icon-success text-green-400"
                                  onClick={() =>
                                    handleShowReminderInfo(eventId)
                                  }
                                  title="Reminder Info"
                                >
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </button>
                              </>
                            )}
                            <button
                              className="light-icon-primary text-blue-400"
                              onClick={() => handleOpenReminderModal(event)}
                              title="Set reminder"
                            >
                              <svg
                                style={{ color: "#60a5fa" }}
                                className="w-4 h-4 sm:w-5 sm:h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                />
                              </svg>
                            </button>
                            {event["Case No"] == "" && <button
                              className="text-blue-400"
                              onClick={() => handleEditEvent(event)}
                            >
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                            }
                            <button
                              className="light-icon-danger text-red-400"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to delete this event?"
                                  )
                                )
                                  handleDeleteEvent(event.docId);
                              }}
                            >
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {googleEvents.map((event) => {
                      // Process title and description for Google events
                      let displayTitle = event.title;
                      let displayDescription = event.description || "";

                      // Check if title starts with "Reminder:" or "Update Next Hearing:"
                      if (event.title.startsWith("Reminder:")) {
                        displayTitle = event.title.replace("Reminder:", "").trim();
                        displayDescription = event.title; // Full title goes to description
                      } else if (event.title.startsWith("Update Next Hearing:")) {
                        displayTitle = event.title.replace("Update Next Hearing:", "").trim();
                        displayDescription = event.title; // Full title goes to description
                      }

                      return (
                        <div
                          key={event.id}
                          className="flex items-center justify-between bg-white bg-opacity-5 p-3 sm:p-4 rounded-lg"
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-1">
                            <div className="w-1 h-10 sm:h-12 bg-blue-500 rounded-full"></div>
                            <div className="flex-1 min-w-0">
                              <div className="event-title text-sm sm:text-base font-semibold">
                                {displayTitle}{" "}
                                <span className="text-xs text-blue-300">
                                  (Google)
                                </span>
                              </div>
                              {displayDescription && (
                                <div className="event-description text-xs sm:text-sm text-gray-300 italic mt-1 line-clamp-2">
                                  {displayDescription}
                                </div>
                              )}
                              <div className="event-time text-xs sm:text-sm text-gray-400 mt-1">
                                {event.time}
                              </div>
                            </div>
                          </div>
                          <button
                            className="text-blue-400"
                            onClick={() => handleEditEvent(event)}
                          >
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>

            <div className="order-1 lg:order-2 z-0 overflow-x-auto overflow-y-hidden">
              <div className="p-4">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleExportCauseList}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Export Cause List
                  </button>
                </div>
                <Calendar
                  onChange={handleDateChange}
                  value={selectedDate}
                  tileContent={tileContent}
                  className="border-none rounded-lg"
                />
              </div>
            </div>
          </div>
          <div className="min-h-screen bg-gray-900 mt-8">
            <div className="bg-gray-800 text-white p-4 relative z-0">
              <h1 className="text-2xl font-bold">My Google Calendar</h1>
            </div>
            <main className="p-4">
              <WeeklyCalendar
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                lastSyncTime={lastSyncTime}
              />
            </main>
          </div>
        </main>
      </div>
      {showAddModal && (
        <AddEventModal
          showModal={showAddModal}
          setShowModal={setShowAddModal}
          userId={userId}
          selectedDate={selectedDate}
          onEventAdded={handleEventAdded}
        />
      )}
      {showEditModal && currentEvent && (
        <EditEventModal
          onClose={() => {
            setShowEditModal(false);
            setCurrentEvent(null);
          }}
          onEventUpdated={handleEventUpdated}
          event={currentEvent}
        />
      )}
      {showReminderModal && eventForReminder && (
        <ReminderModal
          showModal={showReminderModal}
          setShowModal={setShowReminderModal}
          event={eventForReminder}
          onSendReminders={handleSendReminders}
          currentUserId={userId}
          isSending={isSendingReminder}
        />
      )}

      {showReminderInfoModal && (
        <ReminderInfoModal
          showModal={showReminderInfoModal}
          setShowModal={setShowReminderInfoModal}
          reminders={remindersByEvent[selectedEventId] || []}
        />
      )}

      <EventAddedInfoModal
        showModal={showEventAddedInfoModal}
        setShowModal={setShowEventAddedInfoModal}
      />
    </div>
  );
}

export default TaskOverview;
